/**
 * @file Embed URLs and player messaging for dashboard Learn page videos
 */
import { trackEvent } from '@@/bits/analytics'
import { getHyphenatedCurrentLocale } from '@@/bits/current_locale'
import { captureMessage } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { loadJs } from '@@/bits/load'
import { YouTubeVideo } from '@@/bits/youtube_embed_url_constructor'
import type { LearnVideo } from '@@/pinia/dash_learn_store'

type LearnVideoProvider = 'wistia' | 'youtube'

interface WistiaVideoHandle {
  bind: (event: 'end', callback: () => void) => void
  unbind: (event: 'end', callback: () => void) => void
}

interface WistiaQueueEntry {
  id: string
  onReady: (video: WistiaVideoHandle) => void
}

const YOUTUBE_EMBED_ORIGIN = 'https://www.youtube-nocookie.com'
// https://developers.google.com/youtube/iframe_api_reference#Events
const YOUTUBE_PLAYER_STATE_ENDED = 0

// https://docs.wistia.com/docs/javascript-player-api
const WISTIA_PLAYER_API_URL = 'https://fast.wistia.net/assets/external/E-v1.js'

// Caption track codes on our Learn videos (Wistia Captions API): ISO 639-2/B, except zh-Hant. Locales without a track keep Wistia's default.
const WISTIA_CAPTION_LANGUAGES: Record<string, string> = {
  'zh-tw': 'zh-Hant',
  ar: 'ara',
  bg: 'bul',
  bn: 'ben',
  ca: 'cat',
  cs: 'cze',
  da: 'dan',
  de: 'ger',
  el: 'gre',
  en: 'eng',
  es: 'spa',
  et: 'est',
  fa: 'per',
  fi: 'fin',
  fr: 'fre',
  he: 'heb',
  hi: 'hin',
  hr: 'hrv',
  hu: 'hun',
  id: 'ind',
  it: 'ita',
  ja: 'jpn',
  ko: 'kor',
  lv: 'lav',
  ms: 'may',
  nl: 'dut',
  no: 'nor',
  pl: 'pol',
  pt: 'por',
  qs: 'eng',
  qy: 'eng',
  ro: 'rum',
  ru: 'rus',
  sk: 'slo',
  sr: 'srp',
  sv: 'swe',
  th: 'tha',
  tl: 'tgl',
  tr: 'tur',
  uk: 'ukr',
  ur: 'urd',
  vi: 'vie',
  zh: 'chi',
}

function wistiaCaptionLanguage(locale: string): string | null {
  const normalizedLocale = locale.toLowerCase()
  return WISTIA_CAPTION_LANGUAGES[normalizedLocale] ?? WISTIA_CAPTION_LANGUAGES[normalizedLocale.split('-')[0]] ?? null
}

function learnVideoProvider(video: LearnVideo['video']): LearnVideoProvider {
  return isAppUsing('learnWistiaVideos') && video.wistia != null ? 'wistia' : 'youtube'
}

function learnVideoEmbedUrl(video: LearnVideo['video'], { muted = false } = {}): string {
  if (learnVideoProvider(video) === 'wistia' && video.wistia != null) {
    const url = new URL(video.wistia.embedUrl)
    url.searchParams.set('autoPlay', 'true')
    if (muted) url.searchParams.set('muted', 'true')
    url.searchParams.set('plugin[captions][onByDefault]', 'true')
    const captionLanguage = wistiaCaptionLanguage(getHyphenatedCurrentLocale())
    if (captionLanguage != null) url.searchParams.set('plugin[captions][language]', captionLanguage)
    return url.toString()
  }
  const youtubeVideo = new YouTubeVideo(video.videoEmbedUrl).autoplay()
  if (muted) youtubeVideo.mute()
  return youtubeVideo.captions().showSameChannelRelatedVideos().enablejsapi().setCurrentLang().embedUrl
}

function learnVideoDurationInSeconds(video: LearnVideo['video']): number {
  if (learnVideoProvider(video) === 'wistia' && video.wistia != null) return video.wistia.durationInSeconds
  return video.videoDurationInSeconds
}

function learnVideoAnalyticsProperties(video: LearnVideo['video']): { provider: LearnVideoProvider; videoUrl: string } {
  const provider = learnVideoProvider(video)
  const videoUrl = provider === 'wistia' && video.wistia != null ? video.wistia.embedUrl : video.videoEmbedUrl
  return { provider, videoUrl }
}

function wistiaMediaId(iframe: HTMLIFrameElement): string | null {
  return iframe.src.match(/^https:\/\/fast\.wistia\.net\/embed\/iframe\/([a-z0-9]+)/i)?.[1] ?? null
}

let hasReportedWistiaPlayerApiFailure = false

// Without the player API, Wistia videos still play but the playlist can't auto-advance. Reported once per page load.
function reportWistiaPlayerApiFailure(mediaId: string): void {
  if (hasReportedWistiaPlayerApiFailure) return
  hasReportedWistiaPlayerApiFailure = true
  trackEvent('Padlet Learn', 'Wistia player API failed to load', null, null, { mediaId })
  captureMessage('Wistia player API failed to load', { context: { mediaId, scriptUrl: WISTIA_PLAYER_API_URL } })
}

// The player API attaches to Wistia iframes already on the page and runs queued callbacks once each player is ready
function onWistiaVideoReady(mediaId: string, onReady: (video: WistiaVideoHandle) => void): void {
  const wistiaWindow = window as typeof window & { _wq?: WistiaQueueEntry[] }
  wistiaWindow._wq = wistiaWindow._wq ?? []
  wistiaWindow._wq.push({ id: mediaId, onReady })
  loadJs(WISTIA_PLAYER_API_URL).catch(() => reportWistiaPlayerApiFailure(mediaId))
}

function onLearnVideoEnded(iframe: HTMLIFrameElement, callback: () => void): () => void {
  const mediaId = wistiaMediaId(iframe)
  if (mediaId != null) {
    let isListening = true
    let boundVideo: WistiaVideoHandle | null = null
    const handleEnd = (): void => {
      if (isListening) callback()
    }
    onWistiaVideoReady(mediaId, (video) => {
      if (!isListening) return
      boundVideo = video
      video.bind('end', handleEnd)
    })
    return () => {
      isListening = false
      boundVideo?.unbind('end', handleEnd)
    }
  }

  const handleMessage = (event: MessageEvent): void => {
    // The page can have other YouTube players, such as the Learn page's autoplaying featured video.
    if (event.origin !== YOUTUBE_EMBED_ORIGIN || event.source !== iframe.contentWindow) return
    let data
    try {
      data = JSON.parse(event.data)
    } catch {
      return
    }
    if (data?.info?.playerState === YOUTUBE_PLAYER_STATE_ENDED) callback()
  }
  window.addEventListener('message', handleMessage)

  // YouTube only reports player state once the page announces it is listening
  const listeningTimeout = setTimeout(() => {
    iframe.contentWindow?.postMessage('{"event":"listening"}', '*')
  }, 100)

  return () => {
    clearTimeout(listeningTimeout)
    window.removeEventListener('message', handleMessage)
  }
}

export {
  learnVideoAnalyticsProperties,
  learnVideoDurationInSeconds,
  learnVideoEmbedUrl,
  learnVideoProvider,
  onLearnVideoEnded,
  wistiaCaptionLanguage,
}
export type { LearnVideoProvider }
